import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Avatar,
  Box,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  alpha,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  AccountTree as AccountTreeIcon,
  Person as PersonIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { Tree, TreeNode } from 'react-organizational-chart';
import {
  useGetUsersWithReviewsQuery, User
} from "../../redux/vmsApi";
import {
  useGetOrgChartWithBSCQuery,
  OrgChartNode,
} from '../../redux/balancedScorecardApi';
import { PERFORMANCE_RATINGS } from './bscTypes';
import { generateQuartersForYear } from '../../utils/periodUtil';
import PageWithHeader from "../common/PageWithHeader";
import EmployeeCard from "../common/EmployeeCard";
import { useRouter } from '../../hooks/useRouter';

// Tab panel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// Helper to get grade color
const getGradeColor = (grade: string): string => {
  const rating = PERFORMANCE_RATINGS.find(r => r.grade === grade);
  return rating?.colorCode || '#9e9e9e';
};

// Helper to get score color
const getScoreColor = (score?: number): string => {
  if (!score) return '#9e9e9e';
  if (score >= 100) return '#4caf50';
  if (score >= 85) return '#8bc34a';
  if (score >= 70) return '#ffc107';
  if (score >= 60) return '#ff9800';
  return '#f44336';
};

// Get initials from name
const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Extended node with children for tree structure
interface OrgTreeNode extends OrgChartNode {
  children: OrgTreeNode[];
}

// Build tree from flat data
const buildOrgTree = (nodes: OrgChartNode[]): OrgTreeNode[] => {
  const nodeMap = new Map<string, OrgTreeNode>();
  const roots: OrgTreeNode[] = [];

  // First pass: create all nodes with empty children
  nodes.forEach(node => {
    nodeMap.set(node.jobDescription.id, { ...node, children: [] });
  });

  // Second pass: link children to parents
  nodes.forEach(node => {
    const treeNode = nodeMap.get(node.jobDescription.id)!;
    const parentId = node.jobDescription.reportsToJobDescriptionId;
    
    if (parentId && nodeMap.has(parentId)) {
      const parent = nodeMap.get(parentId)!;
      parent.children.push(treeNode);
    } else {
      // No parent or parent not found = root node
      roots.push(treeNode);
    }
  });

  return roots;
};

// Helper to get grade from score
const getGradeFromScore = (score: number): { grade: string; colorCode: string } | null => {
  const rating = PERFORMANCE_RATINGS.find(r => score >= r.minPercentage && score <= r.maxPercentage);
  return rating ? { grade: rating.grade, colorCode: rating.colorCode || '#9e9e9e' } : null;
};

// Calculate average score for direct reports
const calculateDirectReportsAverage = (node: OrgTreeNode): { avgScore: number; avgGrade: string; count: number } | null => {
  if (node.children.length === 0) return null;
  
  const scoresWithBSC = node.children
    .filter(child => child.latestBSC?.totalWeightedScore !== undefined)
    .map(child => child.latestBSC!.totalWeightedScore!);
  
  if (scoresWithBSC.length === 0) return null;
  
  const avgScore = scoresWithBSC.reduce((sum, score) => sum + score, 0) / scoresWithBSC.length;
  const gradeInfo = getGradeFromScore(avgScore);
  
  return {
    avgScore,
    avgGrade: gradeInfo?.grade || 'N/A',
    count: scoresWithBSC.length,
  };
};

// Org chart node card for the tree visualization
interface OrgNodeCardProps {
  node: OrgTreeNode;
  isExpanded?: boolean;
  onToggle?: () => void;
  hasChildren?: boolean;
  nodeRef?: React.RefObject<HTMLDivElement>;
}

const OrgNodeCard: React.FC<OrgNodeCardProps> = ({ node, isExpanded, onToggle, hasChildren, nodeRef }) => {
  const employeeName = node.employee?.fullName || 'Vacant Position';
  const directReportsAvg = calculateDirectReportsAverage(node);

  return (
    <Paper
      ref={nodeRef}
      elevation={3}
      onClick={hasChildren ? onToggle : undefined}
      data-node-id={node.jobDescription.id}
      sx={{
        display: 'inline-block',
        minWidth: 220,
        maxWidth: 280,
        borderRadius: 2,
        overflow: 'hidden',
        border: '3px solid',
        borderColor: node.latestBSC ? getScoreColor(node.latestBSC.totalWeightedScore) : 'grey.300',
        transition: 'all 0.3s ease-in-out',
        cursor: hasChildren ? 'pointer' : 'default',
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: 8,
        },
      }}
    >
      {/* Header with job title */}
      <Box
        sx={{
          bgcolor: node.latestBSC ? getScoreColor(node.latestBSC.totalWeightedScore) : 'grey.500',
          color: 'white',
          py: 1,
          px: 1.5,
        }}
      >
        <Typography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
          {node.jobDescription.title}
        </Typography>
        {node.jobDescription.department && (
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {node.jobDescription.department}
          </Typography>
        )}
      </Box>

      {/* Body with employee name */}
      <Box sx={{ p: 1.5, bgcolor: 'background.paper', textAlign: 'center' }}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={1.5} mb={1}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: node.employee ? 'primary.main' : 'grey.300',
              fontSize: '1rem',
              fontWeight: 'bold',
            }}
          >
            {node.employee ? getInitials(employeeName) : <PersonIcon />}
          </Avatar>
        </Box>
        
        {/* Employee Name - Prominently displayed */}
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{ 
            color: node.employee ? 'text.primary' : 'text.disabled',
            fontStyle: node.employee ? 'normal' : 'italic',
            mb: 0.5,
          }}
        >
          {employeeName}
        </Typography>

        {/* BSC Score Row */}
        <Box display="flex" justifyContent="center" alignItems="center" gap={1} mt={1}>
          <AssessmentIcon fontSize="small" color="action" />
          {node.latestBSC ? (
            <>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ color: getScoreColor(node.latestBSC.totalWeightedScore) }}
              >
                {node.latestBSC.totalWeightedScore?.toFixed(1)}%
              </Typography>
              {node.latestBSC.finalGrade && (
                <Chip
                  label={node.latestBSC.finalGrade}
                  size="small"
                  sx={{
                    bgcolor: getGradeColor(node.latestBSC.finalGrade),
                    color: 'white',
                    fontWeight: 'bold',
                    height: 20,
                    fontSize: '0.7rem',
                  }}
                />
              )}
            </>
          ) : (
            <Typography variant="caption" color="text.disabled">
              No BSC submitted
            </Typography>
          )}
        </Box>

        {/* Direct Reports Average Score */}
        {directReportsAvg && (
          <Box 
            sx={{ 
              mt: 1.5, 
              pt: 1, 
              borderTop: '1px dashed',
              borderColor: 'divider',
            }}
          >
            <Box display="flex" justifyContent="center" alignItems="center" gap={0.5}>
              <PeopleIcon fontSize="small" sx={{ color: 'info.main', fontSize: 16 }} />
              <Typography variant="caption" color="text.secondary">
                Team Avg ({directReportsAvg.count}):
              </Typography>
            </Box>
            <Box display="flex" justifyContent="center" alignItems="center" gap={1} mt={0.5}>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ color: getScoreColor(directReportsAvg.avgScore) }}
              >
                {directReportsAvg.avgScore.toFixed(1)}%
              </Typography>
              <Chip
                label={directReportsAvg.avgGrade}
                size="small"
                sx={{
                  bgcolor: getGradeFromScore(directReportsAvg.avgScore)?.colorCode || '#9e9e9e',
                  color: 'white',
                  fontWeight: 'bold',
                  height: 18,
                  fontSize: '0.65rem',
                }}
              />
            </Box>
          </Box>
        )}

        {/* Direct reports count (only if no average available) */}
        {node.children.length > 0 && !directReportsAvg && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {node.children.length} direct report{node.children.length > 1 ? 's' : ''} (no BSC data)
          </Typography>
        )}

        {/* Expand/Collapse indicator */}
        {hasChildren && (
          <Box 
            sx={{ 
              mt: 1, 
              pt: 1, 
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              color: 'primary.main',
            }}
          >
            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            <Typography variant="caption" fontWeight="medium">
              {isExpanded ? 'Click to collapse' : `Show ${node.children.length} report${node.children.length > 1 ? 's' : ''}`}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

// Recursive tree node component that renders children
interface RecursiveTreeNodeProps {
  node: OrgTreeNode;
  expandedNodes: Set<string>;
  onToggleNode: (nodeId: string, element: HTMLElement | null) => void;
  isFirstLevel?: boolean;
}

const RecursiveTreeNode: React.FC<RecursiveTreeNodeProps> = ({ node, expandedNodes, onToggleNode, isFirstLevel }) => {
  const nodeId = node.jobDescription.id;
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedNodes.has(nodeId);
  const nodeRef = useRef<HTMLDivElement>(null);
  
  const handleToggle = useCallback(() => {
    onToggleNode(nodeId, nodeRef.current);
  }, [nodeId, onToggleNode]);
  
  return (
    <TreeNode 
      label={
        <OrgNodeCard 
          node={node} 
          isExpanded={isExpanded}
          onToggle={handleToggle}
          hasChildren={hasChildren}
          nodeRef={nodeRef}
        />
      }
    >
      {isExpanded && node.children.map((childNode) => (
        <RecursiveTreeNode 
          key={childNode.jobDescription.id} 
          node={childNode} 
          expandedNodes={expandedNodes}
          onToggleNode={onToggleNode}
        />
      ))}
    </TreeNode>
  );
};

const EmployeeReviewAdmin: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [expandedReviewNodes, setExpandedReviewNodes] = useState<Set<string>>(new Set());
  
  // Employee Reviews tab data - we still need to keep this for potential future use
  const { isLoading: usersIsLoading } = useGetUsersWithReviewsQuery();
  
  // Organogram tab data
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [zoom, setZoom] = useState<number>(1);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  
  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.1, 2));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.3));
  const handleZoomReset = () => setZoom(1);
  
  const handleToggleNode = useCallback((nodeId: string, element: HTMLElement | null) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
    
    // Smooth center scroll to clicked node after state update
    setTimeout(() => {
      if (element && containerRef.current) {
        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        
        // Calculate scroll position to center the element
        const scrollLeft = element.offsetLeft - (containerRect.width / 2) + (elementRect.width / 2);
        const scrollTop = element.offsetTop - (containerRect.height / 2) + (elementRect.height / 2);
        
        container.scrollTo({
          left: Math.max(0, scrollLeft),
          top: Math.max(0, scrollTop),
          behavior: 'smooth',
        });
      }
    }, 150); // Wait for children to render
  }, []);
  
  const handleExpandAll = () => {
    if (!orgChart) return;
    const allNodeIds = orgChart.map(n => n.jobDescription.id);
    setExpandedNodes(new Set(allNodeIds));
  };
  
  const handleCollapseAll = () => {
    setExpandedNodes(new Set());
  };
  
  const { data: orgChart, isLoading: orgChartLoading } = useGetOrgChartWithBSCQuery({
    period: selectedPeriod || undefined,
  });
  const periods = [...generateQuartersForYear(2025), ...generateQuartersForYear(2026)];

  // Build tree structure from flat data
  const orgTree = useMemo(() => {
    if (!orgChart) return [];
    return buildOrgTree(orgChart);
  }, [orgChart]);

  // Auto-expand first 2 levels by default (root nodes expanded, showing their direct children)
  // This results in 3 levels visible: root → 1st level → 2nd level (children)
  useEffect(() => {
    if (orgTree.length > 0 && expandedNodes.size === 0) {
      const defaultExpandedIds = new Set<string>();
      // Only add root level nodes - this expands them to show their children
      orgTree.forEach(rootNode => {
        defaultExpandedIds.add(rootNode.jobDescription.id);
      });
      setExpandedNodes(defaultExpandedIds);
      setExpandedReviewNodes(defaultExpandedIds);
    }
  }, [orgTree]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Employee Reviews tab - toggle node expansion
  const handleToggleReviewNode = (nodeId: string) => {
    setExpandedReviewNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  // Convert OrgChartEmployee to User for EmployeeCard
  const orgEmployeeToUser = (node: OrgTreeNode): User | null => {
    if (!node.employee) return null;
    return {
      id: node.employee.id,
      email: node.employee.email,
      name: node.employee.fullName,
      department: node.jobDescription.department,
      gender: '', // Not available in OrgChartEmployee
      jobTitle: node.jobDescription.title,
    };
  };

  // Recursive component to render employee reviews tree
  const EmployeeReviewTreeNode: React.FC<{ node: OrgTreeNode; level: number }> = ({ node, level }) => {
    const userData = orgEmployeeToUser(node);
    const isExpanded = expandedReviewNodes.has(node.jobDescription.id);
    const hasChildren = node.children.length > 0;
    const childrenWithEmployees = node.children.filter(child => child.employee);
    
    if (!userData) {
      // Vacant position - still show children if any
      return (
        <>
          {node.children.map(childNode => (
            <EmployeeReviewTreeNode key={childNode.jobDescription.id} node={childNode} level={level} />
          ))}
        </>
      );
    }
    
    return (
      <Box sx={{ mb: 2, ml: level * 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          {hasChildren && (
            <IconButton 
              onClick={() => handleToggleReviewNode(node.jobDescription.id)}
              size="small"
              sx={{ mt: 1 }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {node.jobDescription.title}
              </Typography>
              {childrenWithEmployees.length > 0 && (
                <Chip 
                  size="small" 
                  label={`${childrenWithEmployees.length} direct report${childrenWithEmployees.length > 1 ? 's' : ''}`}
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              )}
            </Box>
            <EmployeeCard userData={userData} />
          </Box>
        </Box>
        
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ borderLeft: '2px solid', borderColor: 'divider', ml: 2, pl: 2, mt: 1 }}>
            {node.children.map(childNode => (
              <EmployeeReviewTreeNode key={childNode.jobDescription.id} node={childNode} level={0} />
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  };

  const handlePeriodChange = (event: SelectChangeEvent) => {
    setSelectedPeriod(event.target.value);
  };

  const router = useRouter();

  return (
    <PageWithHeader>
      <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => router.push('/reviews')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4">Employee Reviews Admin</Typography>
          </Box>
        </Box>

        <Paper elevation={3} sx={{ p: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab icon={<PeopleIcon />} iconPosition="start" label="Employee Reviews" />
              <Tab icon={<AccountTreeIcon />} iconPosition="start" label="Organogram" />
            </Tabs>
          </Box>

          {/* Employee Reviews Tab */}
          <TabPanel value={tabValue} index={0}>
            {orgChartLoading && (
              <Box display="flex" justifyContent="center" alignItems="center" py={10}>
                <CircularProgress size={60} />
              </Box>
            )}
            {!orgChartLoading && orgTree.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <PeopleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No Employees Found
                </Typography>
                <Typography color="text.secondary">
                  Add job descriptions and assign employees to see their reviews here.
                </Typography>
              </Box>
            )}
            {!orgChartLoading && orgTree.length > 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Click on employees with direct reports to expand and view their team.
                </Typography>
                {orgTree.map(node => (
                  <EmployeeReviewTreeNode key={node.jobDescription.id} node={node} level={0} />
                ))}
              </Box>
            )}
          </TabPanel>

          {/* Organogram Tab */}
          <TabPanel value={tabValue} index={1}>
            {/* Header with period filter */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 2,
                background: (theme) =>
                  `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.1)} 100%)`,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Box>
                  <Typography variant="h6" fontWeight="bold" color="primary.dark">
                    <AccountTreeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Organization Chart
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View organizational structure with BSC scores - showing who reports to whom
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={2}>
                  {/* Expand/Collapse Controls */}
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Tooltip title="Expand All">
                      <IconButton onClick={handleExpandAll} size="small" color="primary">
                        <ExpandMoreIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Collapse All">
                      <IconButton onClick={handleCollapseAll} size="small" color="primary">
                        <ExpandLessIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  
                  {/* Zoom Controls */}
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Tooltip title="Zoom Out">
                      <IconButton onClick={handleZoomOut} size="small" disabled={zoom <= 0.3}>
                        <ZoomOutIcon />
                      </IconButton>
                    </Tooltip>
                    <Chip 
                      label={`${Math.round(zoom * 100)}%`} 
                      size="small" 
                      variant="outlined" 
                      sx={{ minWidth: 60 }}
                    />
                    <Tooltip title="Zoom In">
                      <IconButton onClick={handleZoomIn} size="small" disabled={zoom >= 2}>
                        <ZoomInIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset Zoom">
                      <IconButton onClick={handleZoomReset} size="small">
                        <RestartAltIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Filter by Period</InputLabel>
                    <Select
                      value={selectedPeriod}
                      onChange={handlePeriodChange}
                      label="Filter by Period"
                      size="small"
                    >
                      <MenuItem value="">
                        <em>All Periods (Latest)</em>
                      </MenuItem>
                      {periods.map((period) => (
                        <MenuItem key={period} value={period}>
                          {period}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Paper>

            {/* Loading state */}
            {orgChartLoading && (
              <Box display="flex" justifyContent="center" alignItems="center" py={10}>
                <CircularProgress size={60} />
              </Box>
            )}

            {/* Org Chart Tree */}
            {!orgChartLoading && orgTree.length > 0 && (
              <Box
                ref={containerRef}
                sx={{
                  overflowX: 'auto',
                  overflowY: 'auto',
                  maxHeight: 'calc(100vh - 350px)',
                  minHeight: 400,
                  py: 4,
                  px: 2,
                  bgcolor: '#fafafa',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  scrollBehavior: 'smooth',
                }}
              >
                <Box
                  sx={{
                    transform: `scale(${zoom})`,
                    transformOrigin: 'top center',
                    transition: 'transform 0.2s ease-in-out',
                    minWidth: 'fit-content',
                  }}
                >
                <Tree
                  lineWidth="2px"
                  lineColor="#1976d2"
                  lineBorderRadius="10px"
                  label={
                    <Paper
                      elevation={4}
                      sx={{
                        display: 'inline-block',
                        px: 3,
                        py: 1.5,
                        bgcolor: 'primary.main',
                        color: 'white',
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="h6" fontWeight="bold">
                        West Properties
                      </Typography>
                    </Paper>
                  }
                >
                  {orgTree.map((node) => (
                    <RecursiveTreeNode
                      key={node.jobDescription.id}
                      node={node}
                      expandedNodes={expandedNodes}
                      onToggleNode={handleToggleNode}
                      isFirstLevel
                    />
                  ))}
                </Tree>
                </Box>
              </Box>
            )}

            {/* Empty state */}
            {!orgChartLoading && orgTree.length === 0 && (
              <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                <AccountTreeIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No Job Descriptions Found
                </Typography>
                <Typography color="text.secondary">
                  Add job descriptions to see the organization chart.
                </Typography>
              </Paper>
            )}
          </TabPanel>
        </Paper>
      </Box>
    </PageWithHeader>
  );
};

export default EmployeeReviewAdmin;
